// GameClient class for WebSocket communication with the game server
// Handles connection management, input sending, and state updates

import {
  WebSocketMessage,
  MessageType,
  LobbyCreateMessage,
  LobbyCreatedMessage,
  PlayerJoinMessage,
  PlayerInputMessage,
  GameStartMessage,
  GameStateMessage,
  PlayerJoinedMessage,
  PlayerLeftMessage,
  GameStartedMessage,
  GameEndedMessage,
  PlayerEliminatedMessage,
  ErrorMessage,
  InputEvent,
  InputType,
  StateSnapshot
} from '../shared/types';

export interface GameClientCallbacks {
  onLobbyCreated?: (lobbyId: string) => void;
  onStateUpdate?: (state: StateSnapshot) => void;
  onPlayerJoined?: (player: any, playerCount: number) => void;
  onPlayerLeft?: (playerId: string, playerCount: number) => void;
  onGameStarted?: (startTime: number, players: any[]) => void;
  onGameEnded?: (winnerId: string | null, finalState: StateSnapshot, scoreboard: Record<string, number>) => void;
  onPlayerEliminated?: (playerId: string, reason: string) => void;
  onError?: (code: string, message: string) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

export class GameClient {
  private ws: WebSocket | null = null;
  private callbacks: GameClientCallbacks = {};
  private playerId: string | null = null;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000; // Start with 1 second
  private inputSequence: number = 0;
  private pingInterval: number | null = null;

  constructor(callbacks: GameClientCallbacks = {}) {
    this.callbacks = callbacks;
  }

  /**
   * Creates a new lobby
   * Requirements: 1.2
   */
  async createLobby(): Promise<string> {
    try {
      console.log('Creating lobby via HTTP API...');
      
      const response = await fetch('https://multiplayer-endless-runner.pascal-cf-monin.workers.dev/api/create', {
        method: 'POST'
        // Direct worker URL with CORS headers properly configured
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json() as { lobbyId?: string; error?: string };
      
      if (data.lobbyId) {
        console.log('Lobby created successfully:', data.lobbyId);
        return data.lobbyId;
      } else {
        throw new Error(data.error || 'No lobby ID in response');
      }
      
    } catch (error) {
      console.error('Failed to create lobby:', error);
      // Provide more detailed error information
      if (error instanceof Error) {
        throw new Error(`Failed to create game lobby: ${error.message}`);
      } else {
        throw new Error('Failed to create game lobby: Unknown error');
      }
    }
  }

  /**
   * Connects to the game server WebSocket
   * Enhanced with comprehensive error handling and validation
   * Requirements: 4.1, 4.2, 8.5
   */
  async connect(lobbyId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Validate lobby ID format before attempting connection
        if (!this.validateLobbyId(lobbyId)) {
          const error = new Error('Invalid lobby ID format');
          if (this.callbacks.onError) {
            this.callbacks.onError('INVALID_LOBBY_ID', 'The lobby URL is invalid or malformed');
          }
          reject(error);
          return;
        }

        // Check if already connected
        if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
          console.warn('Already connected to WebSocket');
          resolve();
          return;
        }

        // Clean up any existing connection
        if (this.ws) {
          this.ws.close();
          this.ws = null;
        }

        // Construct WebSocket URL with validation - connect directly to worker
        const protocol = 'wss:'; // Always use secure WebSocket for production
        const wsUrl = `${protocol}//multiplayer-endless-runner.pascal-cf-monin.workers.dev/ws/lobby/${encodeURIComponent(lobbyId)}`;
        
        console.log('Connecting to WebSocket:', wsUrl);
        
        // Set connection timeout
        const connectionTimeout = setTimeout(() => {
          if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
            this.ws.close();
            const error = new Error('Connection timeout');
            if (this.callbacks.onError) {
              this.callbacks.onError('CONNECTION_TIMEOUT', 'Connection to server timed out');
            }
            reject(error);
          }
        }, 10000); // 10 second timeout

        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          clearTimeout(connectionTimeout);
          console.log('WebSocket connected');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          
          // Start ping interval to keep connection alive
          this.startPingInterval();
          
          if (this.callbacks.onConnected) {
            this.callbacks.onConnected();
          }
          
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            this.handleMessage(event.data);
          } catch (error) {
            console.error('Error handling WebSocket message:', error);
            if (this.callbacks.onError) {
              this.callbacks.onError('MESSAGE_PARSE_ERROR', 'Failed to process server message');
            }
          }
        };

        this.ws.onclose = (event) => {
          clearTimeout(connectionTimeout);
          console.log('WebSocket closed:', event.code, event.reason);
          this.isConnected = false;
          this.stopPingInterval();
          
          // Provide user-friendly disconnect reasons
          const disconnectReason = this.getDisconnectReason(event.code, event.reason);
          
          if (this.callbacks.onDisconnected) {
            this.callbacks.onDisconnected();
          }
          
          // Attempt reconnection if not a normal closure and not due to invalid lobby
          if (event.code !== 1000 && event.code !== 1003 && this.reconnectAttempts < this.maxReconnectAttempts) {
            console.log(`Connection lost: ${disconnectReason}. Attempting to reconnect...`);
            this.attemptReconnection(lobbyId);
          } else if (event.code === 1003) {
            // Invalid data - likely invalid lobby ID
            if (this.callbacks.onError) {
              this.callbacks.onError('INVALID_LOBBY', 'The lobby does not exist or is no longer available');
            }
          }
        };

        this.ws.onerror = (error) => {
          clearTimeout(connectionTimeout);
          console.error('WebSocket error:', error);
          
          // Enhanced error reporting
          const errorMessage = this.getConnectionErrorMessage();
          
          if (this.callbacks.onError) {
            this.callbacks.onError('CONNECTION_ERROR', errorMessage);
          }
          reject(new Error(errorMessage));
        };

      } catch (error) {
        console.error('Error creating WebSocket connection:', error);
        if (this.callbacks.onError) {
          this.callbacks.onError('CONNECTION_SETUP_ERROR', 'Failed to initialize connection');
        }
        reject(error);
      }
    });
  }

  /**
   * Validates lobby ID format
   */
  private validateLobbyId(lobbyId: string): boolean {
    if (!lobbyId || typeof lobbyId !== 'string') {
      return false;
    }
    
    // Basic validation - should be non-empty and reasonable length
    if (lobbyId.trim().length < 5 || lobbyId.length > 100) {
      return false;
    }
    
    // Check for invalid characters that could cause issues
    const invalidChars = /[<>\"'&\s]/;
    if (invalidChars.test(lobbyId)) {
      return false;
    }
    
    return true;
  }

  /**
   * Get user-friendly disconnect reason
   */
  private getDisconnectReason(code: number, reason: string): string {
    switch (code) {
      case 1000:
        return 'Normal closure';
      case 1001:
        return 'Server going away';
      case 1002:
        return 'Protocol error';
      case 1003:
        return 'Invalid data received';
      case 1006:
        return 'Connection lost unexpectedly';
      case 1011:
        return 'Server error';
      case 1012:
        return 'Server restarting';
      case 1013:
        return 'Server temporarily unavailable';
      case 1014:
        return 'Bad gateway';
      case 1015:
        return 'TLS handshake failure';
      default:
        return reason || `Connection closed (code: ${code})`;
    }
  }

  /**
   * Get user-friendly connection error message
   */
  private getConnectionErrorMessage(): string {
    if (!navigator.onLine) {
      return 'No internet connection. Please check your network and try again.';
    }
    
    // Check if it's likely a server issue
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) {
      return 'Cannot connect to local server. Make sure the server is running.';
    }
    
    return 'Unable to connect to game server. Please try again in a moment.';
  }

  /**
   * Joins the lobby with a pseudonym
   * Requirements: 2.1, 2.3
   */
  async joinLobby(pseudonym: string): Promise<void> {
    if (!this.isConnected || !this.ws) {
      throw new Error('Not connected to server');
    }

    const message: WebSocketMessage = {
      type: MessageType.PLAYER_JOIN,
      payload: { pseudonym } as PlayerJoinMessage,
      timestamp: Date.now()
    };

    this.ws.send(JSON.stringify(message));
  }

  /**
   * Starts the game (host only)
   * Requirements: 2.3
   */
  async startGame(): Promise<void> {
    if (!this.isConnected || !this.ws) {
      throw new Error('Not connected to server');
    }

    const message: WebSocketMessage = {
      type: MessageType.GAME_START,
      payload: {} as GameStartMessage,
      timestamp: Date.now()
    };

    this.ws.send(JSON.stringify(message));
  }

  /**
   * Sends a jump input event to the server
   * Requirements: 4.1, 4.2
   */
  sendJumpInput(): void {
    if (!this.isConnected || !this.ws || !this.playerId) {
      console.warn('Cannot send input: not connected or not joined');
      return;
    }

    const inputEvent: InputEvent = {
      playerId: this.playerId,
      type: InputType.JUMP,
      timestamp: Date.now(),
      sequence: ++this.inputSequence
    };

    this.sendInputEvent(inputEvent);
  }

  /**
   * Sends a move left input event to the server
   * Requirements: 4.2, 4.5
   */
  sendMoveLeftInput(): void {
    if (!this.isConnected || !this.ws || !this.playerId) {
      console.warn('Cannot send input: not connected or not joined');
      return;
    }

    const inputEvent: InputEvent = {
      playerId: this.playerId,
      type: InputType.MOVE_LEFT,
      timestamp: Date.now(),
      sequence: ++this.inputSequence
    };

    this.sendInputEvent(inputEvent);
  }

  /**
   * Sends a move right input event to the server
   * Requirements: 4.2, 4.5
   */
  sendMoveRightInput(): void {
    if (!this.isConnected || !this.ws || !this.playerId) {
      console.warn('Cannot send input: not connected or not joined');
      return;
    }

    const inputEvent: InputEvent = {
      playerId: this.playerId,
      type: InputType.MOVE_RIGHT,
      timestamp: Date.now(),
      sequence: ++this.inputSequence
    };

    this.sendInputEvent(inputEvent);
  }

  /**
   * Sends an input event to the server (enhanced version)
   * Requirements: 4.1, 4.2
   */
  sendInputEvent(inputEvent: InputEvent): void {
    if (!this.isConnected || !this.ws) {
      console.warn('Cannot send input: not connected');
      return;
    }

    const message: WebSocketMessage = {
      type: MessageType.PLAYER_INPUT,
      payload: { input: inputEvent } as PlayerInputMessage,
      timestamp: Date.now()
    };

    this.ws.send(JSON.stringify(message));
  }

  /**
   * Disconnects from the server
   */
  disconnect(): void {
    if (this.ws) {
      this.stopPingInterval();
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.isConnected = false;
    this.playerId = null;
  }

  /**
   * Gets the current connection status
   */
  isConnectionActive(): boolean {
    return this.isConnected && this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Gets the current player ID
   */
  getPlayerId(): string | null {
    return this.playerId;
  }

  /**
   * Handles incoming WebSocket messages
   */
  private handleMessage(data: string): void {
    try {
      const message: WebSocketMessage = JSON.parse(data);
      
      switch (message.type) {
        case MessageType.LOBBY_CREATED:
          this.handleLobbyCreated(message.payload as LobbyCreatedMessage);
          break;
          
        case MessageType.PLAYER_JOINED:
          this.handlePlayerJoined(message.payload as PlayerJoinedMessage);
          break;
          
        case MessageType.PLAYER_LEFT:
          this.handlePlayerLeft(message.payload as PlayerLeftMessage);
          break;
          
        case MessageType.GAME_STATE:
          this.handleGameState(message.payload as GameStateMessage);
          break;
          
        case MessageType.GAME_STARTED:
          this.handleGameStarted(message.payload as GameStartedMessage);
          break;
          
        case MessageType.GAME_ENDED:
          this.handleGameEnded(message.payload as GameEndedMessage);
          break;
          
        case MessageType.PLAYER_ELIMINATED:
          this.handlePlayerEliminated(message.payload as PlayerEliminatedMessage);
          break;
          
        case MessageType.ERROR:
          this.handleError(message.payload as ErrorMessage);
          break;
          
        case MessageType.SCOREBOARD_UPDATE:
          // Handle scoreboard update (can be ignored for now or logged)
          console.log('Scoreboard updated:', message.payload);
          break;
          
        case MessageType.PONG:
          // Handle pong response (connection keep-alive)
          break;
          
        default:
          console.warn('Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  /**
   * Handles lobby created messages
   */
  private handleLobbyCreated(payload: LobbyCreatedMessage): void {
    if (this.callbacks.onLobbyCreated) {
      this.callbacks.onLobbyCreated(payload.lobbyId);
    }
  }

  /**
   * Handles player joined messages
   */
  private handlePlayerJoined(payload: PlayerJoinedMessage): void {
    // If this is our own join confirmation, store our player ID
    if (!this.playerId) {
      this.playerId = payload.player.id;
    }
    
    if (this.callbacks.onPlayerJoined) {
      this.callbacks.onPlayerJoined(payload.player, payload.playerCount);
    }
  }

  /**
   * Handles player left messages
   */
  private handlePlayerLeft(payload: PlayerLeftMessage): void {
    if (this.callbacks.onPlayerLeft) {
      this.callbacks.onPlayerLeft(payload.playerId, payload.playerCount);
    }
  }

  /**
   * Handles game state updates
   */
  private handleGameState(payload: GameStateMessage): void {
    if (this.callbacks.onStateUpdate) {
      this.callbacks.onStateUpdate(payload.state);
    }
  }

  /**
   * Handles game started messages
   */
  private handleGameStarted(payload: GameStartedMessage): void {
    if (this.callbacks.onGameStarted) {
      this.callbacks.onGameStarted(payload.startTime, payload.players);
    }
  }

  /**
   * Handles game ended messages
   */
  private handleGameEnded(payload: GameEndedMessage): void {
    if (this.callbacks.onGameEnded) {
      this.callbacks.onGameEnded(payload.winnerId, payload.finalState, payload.scoreboard);
    }
  }

  /**
   * Handles player eliminated messages
   */
  private handlePlayerEliminated(payload: PlayerEliminatedMessage): void {
    if (this.callbacks.onPlayerEliminated) {
      this.callbacks.onPlayerEliminated(payload.playerId, payload.reason);
    }
  }

  /**
   * Handles error messages
   */
  private handleError(payload: ErrorMessage): void {
    console.error('Server error:', payload.code, payload.message);
    if (this.callbacks.onError) {
      this.callbacks.onError(payload.code, payload.message);
    }
  }

  /**
   * Attempts to reconnect to the server with exponential backoff
   * Enhanced error handling with connection state management
   */
  private attemptReconnection(lobbyId: string): void {
    this.reconnectAttempts++;
    
    console.log(`Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${this.reconnectDelay}ms`);
    
    // Validate lobby ID before attempting reconnection
    if (!this.validateLobbyId(lobbyId)) {
      console.error('Invalid lobby ID for reconnection:', lobbyId);
      if (this.callbacks.onError) {
        this.callbacks.onError('INVALID_LOBBY_ID', 'Cannot reconnect: invalid lobby ID');
      }
      return;
    }
    
    setTimeout(async () => {
      try {
        // Check network connectivity before attempting reconnection
        if (!navigator.onLine) {
          console.warn('No network connectivity, delaying reconnection');
          this.attemptReconnection(lobbyId); // Retry with same attempt count
          return;
        }

        await this.connect(lobbyId);
        
        // If we had a player ID, try to rejoin automatically
        if (this.playerId) {
          console.log('Attempting to restore session after reconnection');
          // Note: Server should handle session restoration
          // For now, we'll notify the user about the reconnection
          if (this.callbacks.onConnected) {
            this.callbacks.onConnected();
          }
        }
      } catch (error) {
        console.error('Reconnection failed:', error);
        
        // Enhanced error handling based on error type
        const errorMessage = this.getReconnectionErrorMessage(error);
        
        // Exponential backoff with jitter to prevent thundering herd
        const jitter = Math.random() * 1000; // 0-1 second jitter
        this.reconnectDelay = Math.min(this.reconnectDelay * 2 + jitter, 30000); // Max 30 seconds
        
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.attemptReconnection(lobbyId);
        } else {
          console.error('Max reconnection attempts reached');
          if (this.callbacks.onError) {
            this.callbacks.onError('CONNECTION_FAILED', `Unable to reconnect to server: ${errorMessage}`);
          }
        }
      }
    }, this.reconnectDelay);
  }

  /**
   * Get user-friendly error message for reconnection failures
   */
  private getReconnectionErrorMessage(error: any): string {
    if (error instanceof Error) {
      if (error.message.includes('network')) {
        return 'Network connection issue';
      }
      if (error.message.includes('timeout')) {
        return 'Connection timeout';
      }
      if (error.message.includes('refused')) {
        return 'Server unavailable';
      }
      return error.message;
    }
    return 'Unknown connection error';
  }

  /**
   * Starts the ping interval to keep connection alive
   */
  private startPingInterval(): void {
    this.pingInterval = window.setInterval(() => {
      if (this.isConnected && this.ws) {
        const message: WebSocketMessage = {
          type: MessageType.PING,
          payload: {},
          timestamp: Date.now()
        };
        this.ws.send(JSON.stringify(message));
      }
    }, 30000); // Ping every 30 seconds
  }

  /**
   * Stops the ping interval
   */
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}